import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronUp, Filter, Search, SlidersHorizontal, X } from 'lucide-react';
import { useLayout } from '../context/LayoutContext';
import ProjectionsFilters from './ProjectionsFilters';
import {
    FinancialProjectionsViewProps, SortKey,
    formatBillions, formatCurrency, formatPercent, getMethodBadge,
} from './projectionsFormat';
import { StatusFilter, estNonProjete, estPublie, dividendeAffiche, payoutHorsNorme, libelleFiabilite } from './projectionsModel';

const STATUS_TABS: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'Toutes' },
    { key: 'published', label: 'Publiées' },
    { key: 'estimated', label: 'Projetées' },
    { key: 'non_projete', label: 'Non projetées' },
    { key: 'risk', label: 'Divergences' },
];

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
    { key: 'yield_est', label: 'Rendement' },
    { key: 'per_est', label: 'PER' },
    { key: 'var_net_income', label: 'Variation du résultat' },
    { key: 'div_n_est', label: 'Dividende estimé' },
    { key: 'payout_ratio', label: 'Payout' },
];

const pillStyle = (bg: string, border: string): React.CSSProperties => ({
    flexShrink: 0, background: bg, border: `1px solid ${border}`, borderRadius: 14, padding: '10px 14px', minWidth: 110,
});
const pillLabel = (color: string): React.CSSProperties => ({
    fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.05em',
});

const FinancialProjectionsMobile: React.FC<FinancialProjectionsViewProps> = ({
    data, loading, error, cacheDate, annee, annees, onAnneeChange, keyword, setKeyword,
    sortConfig, handleSort, statusFilter, setStatusFilter,
    filters, setFilters, processedData, summaryMetrics,
}) => {
    const navigate = useNavigate();
    const { toggleMobileMenu } = useLayout();
    const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
    const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
    const [showSearch, setShowSearch] = useState(false);

    const toggleCard = (symbol: string) => {
        setExpandedCards(prev => {
            const next = new Set(prev);
            if (next.has(symbol)) next.delete(symbol); else next.add(symbol);
            return next;
        });
    };

    const hasActiveFilters = filters.minYield > 0 || filters.maxPer !== null
        || filters.growthTarget !== 'any' || filters.reliability !== 'any' || filters.riskOnly;

    return (
        <div style={{ paddingBottom: 100 }}>
            {/* ── En-tête collant ── */}
            <div style={{
                position: 'sticky', top: 0, zIndex: 50,
                background: 'var(--bg-main)', borderBottom: '1px solid var(--border-color)',
                padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
            }}>
                <button
                    type="button"
                    onClick={toggleMobileMenu}
                    style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                    aria-label="Menu"
                >
                    <i className="material-icons" style={{ fontSize: '24px', color: 'var(--text-heading)' }}>menu</i>
                </button>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-heading)', lineHeight: 1 }}>
                        Projections {annee ?? ''}
                    </div>
                    {cacheDate && (
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                            Calculé le {new Date(cacheDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                        </div>
                    )}
                </div>
                <button
                    onClick={() => setShowSearch(s => !s)}
                    style={{ background: 'none', border: 'none', padding: 8, cursor: 'pointer', color: 'var(--text-muted)' }}
                    aria-label="Rechercher"
                >
                    <Search size={20} />
                </button>
                <button
                    onClick={() => setShowFiltersDrawer(true)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                        borderRadius: 10, border: `1px solid ${hasActiveFilters ? '#6366f1' : 'var(--border-color)'}`,
                        background: hasActiveFilters ? 'rgba(99,102,241,0.1)' : 'var(--card-bg)',
                        color: hasActiveFilters ? '#6366f1' : 'var(--text-muted)',
                        fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}
                >
                    <SlidersHorizontal size={14} />
                    Filtres{hasActiveFilters ? ' ●' : ''}
                </button>
            </div>

            {/* ── Recherche ── */}
            {showSearch && (
                <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: 8 }}>
                    <input
                        autoFocus
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                        placeholder="Rechercher un symbole…"
                        style={{
                            flex: 1, padding: '10px 14px', borderRadius: 10, fontSize: 14,
                            border: '1px solid var(--border-color)', background: 'var(--card-bg)',
                            color: 'var(--text-heading)', outline: 'none',
                        }}
                    />
                    {keyword && (
                        <button onClick={() => setKeyword('')} aria-label="Effacer la recherche" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                            <X size={18} />
                        </button>
                    )}
                </div>
            )}

            {/* ── Synthèse (hors lignes non projetées) ── */}
            {summaryMetrics && (
                <div style={{ display: 'flex', gap: 10, padding: '12px 16px', overflowX: 'auto', scrollbarWidth: 'none' }}>
                    {summaryMetrics.rendementMax && (
                        <div style={pillStyle('rgba(16,185,129,0.08)', 'rgba(16,185,129,0.2)')}>
                            <div style={pillLabel('#059669')}>Rdt est. max</div>
                            <div style={{ fontSize: 20, fontWeight: 900, color: '#059669' }}>{(summaryMetrics.rendementMax.yield_est as number).toFixed(1)} %</div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{summaryMetrics.rendementMax.symbol}</div>
                        </div>
                    )}
                    {summaryMetrics.perMin && (
                        <div style={pillStyle('rgba(59,130,246,0.08)', 'rgba(59,130,246,0.2)')}>
                            <div style={pillLabel('#2563eb')}>PER est. min</div>
                            <div style={{ fontSize: 20, fontWeight: 900, color: '#2563eb' }}>{(summaryMetrics.perMin.per_est as number).toFixed(1)}x</div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{summaryMetrics.perMin.symbol}</div>
                        </div>
                    )}
                    {summaryMetrics.croissanceMax && summaryMetrics.croissanceMax.var_net_income != null && (
                        <div style={pillStyle('rgba(139,92,246,0.08)', 'rgba(139,92,246,0.2)')}>
                            <div style={pillLabel('#7c3aed')}>Hausse rés. max</div>
                            <div style={{ fontSize: 20, fontWeight: 900, color: '#7c3aed' }}>
                                {summaryMetrics.croissanceMax.var_net_income > 0 ? '+' : ''}{(summaryMetrics.croissanceMax.var_net_income * 100).toFixed(0)} %
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{summaryMetrics.croissanceMax.symbol}</div>
                        </div>
                    )}
                    <div style={pillStyle('rgba(99,102,241,0.06)', 'var(--border-color)')}>
                        <div style={pillLabel('var(--text-muted)')}>Projetées</div>
                        <div style={{ fontSize: 20, fontWeight: 900, color: 'var(--text-heading)' }}>
                            {summaryMetrics.counts.estimated}<span style={{ fontSize: 12, color: 'var(--text-muted)' }}>/{summaryMetrics.counts.total}</span>
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{summaryMetrics.counts.nonProjete} non projetées</div>
                    </div>
                </div>
            )}

            {/* ── Statuts ── */}
            <div style={{ display: 'flex', gap: 8, padding: '0 16px 12px', overflowX: 'auto', scrollbarWidth: 'none' }}>
                {STATUS_TABS.map(tab => {
                    const c = summaryMetrics?.counts;
                    const count = !c ? null
                        : tab.key === 'all' ? c.total
                        : tab.key === 'published' ? c.published
                        : tab.key === 'estimated' ? c.estimated
                        : tab.key === 'non_projete' ? c.nonProjete
                        : c.risk;
                    const active = statusFilter === tab.key;
                    return (
                        <button key={tab.key} onClick={() => setStatusFilter(tab.key)} style={{
                            flexShrink: 0, padding: '7px 14px', borderRadius: 9999,
                            fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            background: active ? '#1e293b' : 'var(--card-bg)',
                            color: active ? 'white' : 'var(--text-muted)',
                            border: `1px solid ${active ? '#1e293b' : 'var(--border-color)'}`,
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                            {tab.label}
                            {count != null && (
                                <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 9999, background: active ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)' }}>
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* ── Tri ── */}
            <div style={{ padding: '0 16px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Trier :</span>
                <select
                    value={sortConfig.key}
                    onChange={e => handleSort(e.target.value as SortKey)}
                    style={{
                        flex: 1, padding: '8px 12px', borderRadius: 10, fontSize: 13,
                        border: '1px solid var(--border-color)', background: 'var(--card-bg)',
                        color: 'var(--text-heading)', fontWeight: 700,
                    }}
                >
                    {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
                <button onClick={() => handleSort(sortConfig.key)} style={{
                    padding: '8px 12px', borderRadius: 10, border: '1px solid var(--border-color)',
                    background: 'var(--card-bg)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12,
                }}>
                    {sortConfig.direction === 'desc' ? '↓ Décroissant' : '↑ Croissant'}
                </button>
            </div>

            <div style={{ padding: '0 16px 8px', fontSize: 12, color: 'var(--text-muted)' }}>
                <Filter size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                {processedData.length === data.length ? `${data.length} valeurs` : `${processedData.length} / ${data.length} valeurs`}
            </div>

            {/* ── Contenu ── */}
            <div style={{ padding: '0 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {loading ? (
                    [...Array(6)].map((_, i) => (
                        <div key={i} style={{ borderRadius: 16, border: '1px solid var(--border-color)', padding: 16, background: 'var(--card-bg)' }}>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--border-color)', animation: 'pulse 1.4s infinite' }} />
                                <div style={{ flex: 1 }}>
                                    <div style={{ height: 14, width: 60, borderRadius: 6, background: 'var(--border-color)', marginBottom: 6 }} />
                                    <div style={{ height: 10, width: 40, borderRadius: 6, background: 'var(--border-color)' }} />
                                </div>
                            </div>
                        </div>
                    ))
                ) : error ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#f43f5e' }}>{error}</div>
                ) : data.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                        <p style={{ fontWeight: 700, color: 'var(--text-heading)' }}>Aucune projection calculée{annee ? ` pour ${annee}` : ''}</p>
                    </div>
                ) : processedData.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                        <p style={{ fontWeight: 700, color: 'var(--text-heading)' }}>Aucun résultat</p>
                        <p style={{ fontSize: 13 }}>Ajustez vos filtres pour voir des valeurs.</p>
                    </div>
                ) : processedData.map(row => {
                    const isExpanded = expandedCards.has(row.symbol);
                    const reliabilityColor = row.reliability_tier === 'FIABLE' ? '#10b981' : row.reliability_tier === 'MOYEN' ? '#f59e0b' : '#f43f5e';
                    const isPublished = estPublie(row);
                    const nonProjete = estNonProjete(row);
                    const dividende = dividendeAffiche(row);
                    const yieldEst = row.yield_est ?? 0;

                    return (
                        <div key={row.symbol} style={{
                            borderRadius: 16, border: '1px solid var(--border-color)',
                            background: 'var(--card-bg)', overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            opacity: nonProjete ? 0.8 : 1,
                        }}>
                            <div style={{ height: 3, background: nonProjete ? 'var(--border-color)' : yieldEst > 8 ? 'linear-gradient(90deg,#10b981,#059669)' : yieldEst > 5 ? 'linear-gradient(90deg,#6366f1,#4f46e5)' : 'var(--border-color)' }} />

                            <div
                                onClick={() => toggleCard(row.symbol)}
                                style={{ padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer' }}
                            >
                                <div style={{ position: 'relative', width: 40, height: 40, flexShrink: 0 }}>
                                    <img
                                        src={`/images/societes/${row.symbol}.png`}
                                        alt={row.symbol}
                                        style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'contain', border: '1px solid var(--border-color)', padding: 3, background: 'white' }}
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = 'none';
                                            const fb = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                                            if (fb) fb.style.display = 'flex';
                                        }}
                                    />
                                    <div style={{ display: 'none', width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg,#6366f1,#4338ca)', color: 'white', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, position: 'absolute', top: 0, left: 0 }}>
                                        {row.symbol.substring(0, 3)}
                                    </div>
                                </div>

                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 16, fontWeight: 900, color: 'var(--text-heading)' }}>{row.symbol}</span>
                                        {nonProjete ? (
                                            <span style={{ fontSize: 8, fontWeight: 900, padding: '2px 5px', borderRadius: 4, background: '#94a3b8', color: 'white', textTransform: 'uppercase' }}>
                                                Non projeté
                                            </span>
                                        ) : row.reliability_tier && (
                                            <span style={{ fontSize: 8, fontWeight: 900, padding: '2px 5px', borderRadius: 4, background: reliabilityColor, color: 'white', textTransform: 'uppercase' }}>
                                                {libelleFiabilite(row.reliability_tier)}
                                            </span>
                                        )}
                                        {isPublished && (
                                            <span style={{ fontSize: 8, fontWeight: 900, padding: '2px 5px', borderRadius: 4, background: '#e0e7ff', color: '#4338ca', textTransform: 'uppercase' }}>
                                                Publié
                                            </span>
                                        )}
                                        {row.growth_capped && (
                                            <span style={{ fontSize: 10, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <AlertTriangle size={10} /> Divergence
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                                        <span style={{ fontSize: 13, fontWeight: 800, color: '#059669' }}>
                                            {row.yield_est ? row.yield_est.toFixed(1) + ' %' : '—'}
                                        </span>
                                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                            PER {row.per_est ? row.per_est.toFixed(1) + 'x' : '—'}
                                        </span>
                                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                            {nonProjete ? '—' : formatPercent(row.var_net_income)}
                                        </span>
                                    </div>
                                </div>

                                <div style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
                                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </div>
                            </div>

                            {isExpanded && (
                                <div style={{ borderTop: '1px solid var(--border-color)', padding: 16 }}>
                                    {nonProjete && (
                                        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px' }}>
                                            Aucune publication de l'exercice {row.year_n} en base : les montants sont ceux de {row.year_prev}, recopiés.
                                        </p>
                                    )}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                                        {[
                                            { label: row.revenue_n_actual ? 'CA publié' : 'CA est.', value: formatBillions(row.revenue_n_actual ?? row.revenue_n_est) },
                                            { label: 'Marge', value: row.net_margin ? row.net_margin.toFixed(1) + ' %' : '—' },
                                            { label: `Rés. ${row.year_prev}`, value: formatBillions(row.net_income_n_1) },
                                            { label: row.net_income_n_actual ? 'Rés. publié' : 'Rés. est.', value: formatBillions(row.net_income_n_actual ?? row.net_income_n_est) },
                                            {
                                                label: dividende.nature === 'REEL' ? (dividende.source === 'ANNONCE' ? 'Div. annoncé' : 'Div. payé') : 'Div. est.',
                                                value: dividende.nature === 'AUCUN' ? 'Aucun' : dividende.nature === 'INCONNU' ? '—' : formatCurrency(dividende.montant),
                                            },
                                            { label: payoutHorsNorme(row) ? 'Payout ⚠' : 'Payout', value: row.payout_ratio ? row.payout_ratio.toFixed(0) + ' %' : '—' },
                                        ].map((m) => (
                                            <div key={m.label} style={{ background: 'var(--bg-secondary,rgba(0,0,0,0.03))', borderRadius: 10, padding: '8px 10px' }}>
                                                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.label}</div>
                                                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-heading)', marginTop: 2 }}>{m.value}</div>
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                                        {(() => { const b = getMethodBadge(row.revenue_actual_method ?? row.revenue_method); return b ? <span className={`method-badge ${b.cls}`}>CA : {b.label}</span> : null; })()}
                                        {(() => { const b = getMethodBadge(row.net_income_actual_method ?? row.net_income_method); return b ? <span className={`method-badge ${b.cls}`}>Rés. : {b.label}</span> : null; })()}
                                        {dividende.nature === 'REEL' && (
                                            <span className={`method-badge ${dividende.source === 'ANNONCE' ? 'method-badge--amber' : 'method-badge--teal'}`}>
                                                Div. : {dividende.source === 'ANNONCE' ? 'annoncé' : 'payé'}
                                            </span>
                                        )}
                                        {dividende.nature === 'AUCUN' && <span className="method-badge method-badge--gray">Pas de dividende</span>}
                                        {payoutHorsNorme(row) && <span className="method-badge method-badge--amber">Payout &gt; 100 % du résultat estimé</span>}
                                    </div>

                                    <button
                                        onClick={() => navigate(`/data?symbol=${row.symbol}`)}
                                        style={{
                                            width: '100%', padding: '12px', borderRadius: 12,
                                            background: 'linear-gradient(135deg,#6366f1,#4f46e5)',
                                            color: 'white', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                                        }}
                                    >
                                        Voir la fiche société →
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* ── Tiroir de filtres ── */}
            {showFiltersDrawer && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end' }}
                    onClick={e => { if (e.target === e.currentTarget) setShowFiltersDrawer(false); }}
                >
                    <div style={{
                        width: '100%', maxHeight: '85vh', background: 'var(--card-bg)',
                        borderTopLeftRadius: 24, borderTopRightRadius: 24,
                        padding: '20px 20px 40px', overflowY: 'auto',
                        animation: 'slideUp 0.25s ease-out',
                    }}>
                        <div style={{ width: 40, height: 4, background: 'var(--border-color)', borderRadius: 2, margin: '0 auto 20px' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text-heading)' }}>Filtres</h3>
                            <button onClick={() => setShowFiltersDrawer(false)} aria-label="Fermer" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                                <X size={20} />
                            </button>
                        </div>
                        <ProjectionsFilters
                            filters={filters}
                            onFiltersChange={setFilters}
                            annees={annees}
                            annee={annee}
                            onAnneeChange={onAnneeChange}
                            totalResults={data.length}
                            filteredResults={processedData.length}
                        />
                        <button
                            onClick={() => setShowFiltersDrawer(false)}
                            style={{
                                marginTop: 16, width: '100%', padding: 14, borderRadius: 14,
                                background: 'linear-gradient(135deg,#6366f1,#4f46e5)',
                                color: 'white', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer',
                            }}
                        >
                            Appliquer ({processedData.length} résultats)
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
                @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
            `}</style>
        </div>
    );
};

export default FinancialProjectionsMobile;
